import { dayjs } from "@thalamos/common";
import { RequirementFriendlyDescription } from "../requirementFriendlyDescription.js";
import { h3Part1DataSchema, h3Part2DataSchema, } from "../schemas/mhaForms/hospital/h3.js";
import { section4Templates } from "../sections.js";
import { formIsApplication, formIsCompleted, formIsValidForProgression, getLatestVersion, } from "../types/form.js";
const SECTION_TEXT = {
    "2": "Section 2",
    "3": "Section 3",
    "4": "Section 4",
};
export function isWithinS15AmendPeriod(data) {
    if (data.length === 1) {
        const section = data[0].data.sectionValue;
        const admissionDateTime = data[0].data.admissionDateTime;
        return dayjs(admissionDateTime)
            .add(section === "4" ? 72 : 14, section === "4" ? "hours" : "days")
            .isAfter(dayjs(), section === "4" ? undefined : "day");
    }
    else if (data[1] !== undefined) {
        const admissionDateTime = data[1].data.secondMedRecReceivedTime;
        return dayjs(admissionDateTime).add(14, "days").isAfter(dayjs(), "day");
    }
    else {
        return false;
    }
}
/**
 * The application this record of detention is filed against.
 *
 * Identity is the form id, never the template id: a redo leaves the void original
 * and its replacement sharing a template, so a template can name two generations
 * of the same application. The H3's `acknowledges` link goes on naming the
 * original through any number of redos, so the lineage is walked via
 * `redoneFromFormId`: a still-valid application in the acknowledged lineage
 * answers as soon as it exists, while one merely being redone goes on answering
 * for the section it was filed under. Restricting both to the lineage keeps an
 * unrelated application elsewhere in the context from being picked up.
 */
function getApplication(context) {
    const linkedIds = new Set(context.linkedForms.filter(formIsApplication).map((link) => link.id));
    const applications = context.previousForms.filter(formIsApplication);
    const applicationsById = new Map(applications.map((form) => [form.id, form]));
    const isInAcknowledgedLineage = (form) => {
        for (let current = form; current !== undefined; current =
            current.redoneFromFormId == null
                ? undefined
                : applicationsById.get(current.redoneFromFormId)) {
            if (linkedIds.has(current.id))
                return true;
        }
        return false;
    };
    return (applications.find((form) => isInAcknowledgedLineage(form) && formIsValidForProgression(form)) ??
        applications.find((form) => linkedIds.has(form.id) && formIsCompleted(form)));
}
// Determines which section of the MHA (2, 3, or 4) should be filled in on the
// form, based on the section the admission context's application form — see
// getApplication — was filed under.
function sectionValueFn(_data, context) {
    const application = getApplication(context);
    const id = application?.formTemplate?.id;
    if (id === undefined)
        return undefined;
    if (id === "mha-a1" || id === "mha-a2")
        return "2";
    if (id === "mha-a5" || id === "mha-a6")
        return "3";
    if (section4Templates.includes(id))
        return "4";
    return undefined;
}
function sectionDefaultFn(data, context) {
    const value = sectionValueFn(data, context);
    const label = SECTION_TEXT[value];
    if (value && label) {
        return { value, label };
    }
    else {
        return undefined;
    }
}
// Pre-fills the hospital from the admission context's application form — see
// getApplication. Undefined when there is none (a standalone H3 created
// outside an admission context).
function hospitalDefaultFn(_data, context) {
    const application = getApplication(context);
    const latestVersionData = getLatestVersion(application)?.data;
    const applicationHospital = latestVersionData?.[0]?.data?.hospital;
    return applicationHospital
        ? { ...applicationHospital, isConfirmed: false }
        : undefined;
}
export const h3Part1Template = {
    signing: {
        userOrTeamRequirement: {
            accreditation: [],
            teamType: ["ward", "mha"],
            prohibitedAccreditations: ["doctor"],
            friendlyDescription: RequirementFriendlyDescription.onBehalfOfResponsibleHospital,
        },
        linkableForms: {
            isBypassable: false,
            isMultiSelect: false,
            confirmAfterCompletion: false,
            source: "form-context",
            formTemplateIds: [
                "mha-a1",
                "mha-a10",
                "mha-a9",
                "mha-a5",
                "mha-a6",
                "mha-a2",
            ],
            checkValidityFn: (forms) => {
                return [
                    {
                        type: "warning",
                        reason: "The section 2 and 3 application is valid for 14 days from the last time the doctor examined the patient. The emergency application is valid for 24 hours from the last time the doctor examined the patient or when the application was made. Please make sure to check the validity of the admission forms before proceeding. If you are unsure, please speak to your mental health act team. Please select the application made for purpose of the detention",
                        isIgnorable: forms.length === 1,
                    },
                ];
            },
        },
    },
    formBuilderFields: [
        {
            type: "hospital-picker",
            field: "hospital",
            label: "Enter name and address of hospital",
            defaultFn: hospitalDefaultFn,
        },
        {
            type: "readonly",
            label: "Patient Name",
            textFn: (data) => [data.patient?.name || ""],
            isReadOnlyPatientInfo: true,
        },
        {
            type: "branch",
            label: "Confirm the correct option",
            field: "patientWasInHospitalBeforeSectioning",
            branches: [
                {
                    fieldValue: true,
                    label: "(a) The patient is not already an in-patient in the hospital",
                    fields: [
                        {
                            type: "date-time",
                            label: "Enter date and time the patient was admitted to hospital",
                            field: "admissionDateTime",
                            maximum: (today) => today,
                        },
                        {
                            type: "dropdown",
                            label: "What section of the MHA are you admitting the patient under?",
                            valueField: "sectionValue",
                            textField: "sectionText",
                            defaultFn: sectionDefaultFn,
                            isReadOnlyFn: (data, context) => !!sectionValueFn(data, context),
                            options: [
                                { value: "2", label: "Section 2" },
                                { value: "3", label: "Section 3" },
                                { value: "4", label: "Section 4" },
                            ],
                        },
                        {
                            type: "label",
                            showStaticLabel: true,
                            label: [
                                "(a) This patient was admitted to this hospital in pursuance of an application for admission under the above stated section of the Mental Health Act 1983.",
                            ],
                        },
                    ],
                    fieldsOwned: ["admissionDateTime", "sectionText", "sectionValue"],
                },
                {
                    fieldValue: false,
                    label: "(b) The patient is already an in-patient",
                    fields: [
                        {
                            type: "dropdown",
                            label: "What section of the MHA are you admitting the patient under? ",
                            valueField: "sectionValue",
                            textField: "sectionText",
                            defaultFn: sectionDefaultFn,
                            isReadOnlyFn: (data, context) => !!sectionValueFn(data, context),
                            options: [
                                { value: "2", label: "Section 2" },
                                { value: "3", label: "Section 3" },
                                { value: "4", label: "Section 4" },
                            ],
                        },
                        {
                            type: "date-time",
                            label: "Enter date and time you received the application for the admission",
                            field: "admissionDateTime",
                            maximum: (today) => today.endOf("day"),
                        },
                        {
                            type: "label",
                            showStaticLabel: true,
                            label: [
                                "(b) An application for this patient (who had already been admitted to this hospital) under the above stated section of the Mental Health Act 1983 was received by me on behalf of the hospital managers.",
                            ],
                        },
                        {
                            type: "label",
                            showStaticLabel: true,
                            label: [
                                "(b) The patient was accordingly treated as admitted for the purpose of the Act from that time.",
                            ],
                        },
                    ],
                    fieldsOwned: ["admissionDateTime", "sectionText", "sectionValue"],
                },
            ],
        },
        {
            type: "practitioner",
            label: "My name",
            field: "user",
            hideEmail: true,
            hideAddress: true,
        },
    ],
    dataSchema: h3Part1DataSchema,
};
export const h3Part2Template = {
    signing: {
        userOrTeamRequirement: {
            accreditation: [],
            teamType: ["ward", "mha"],
            prohibitedAccreditations: ["doctor"],
            friendlyDescription: RequirementFriendlyDescription.onBehalfOfResponsibleHospital,
        },
    },
    autoAssignToPartSignatory: 1,
    formBuilderFields: [
        {
            type: "readonly",
            label: "Patient Name",
            textFn: (data) => [data.patient?.name || ""],
            isReadOnlyPatientInfo: true,
        },
        {
            type: "date-time",
            label: "Enter date and time the second medical recommendation was received",
            field: "secondMedRecReceivedTime",
            maximum: (now) => now.endOf("day"),
        },
        {
            type: "practitioner",
            label: "My name",
            field: "user",
            hideEmail: true,
            hideAddress: true,
        },
    ],
    dataSchema: h3Part2DataSchema,
};
export const h3V1Base = {
    id: "mha-h3",
    category: "hospital",
    version: "1.0",
    status: "active",
    parts: [h3Part1Template, h3Part2Template],
    formName: "H3",
    section: "2, 3 and 4",
    description: "Record of detention in Hospital",
    linkableForms: [
        { templateId: "mha-a1", reason: "acknowledges" },
        { templateId: "mha-a2", reason: "acknowledges" },
        { templateId: "mha-a5", reason: "acknowledges" },
        { templateId: "mha-a6", reason: "acknowledges" },
        { templateId: "mha-a10", reason: "acknowledges" },
        { templateId: "mha-a9", reason: "acknowledges" },
    ],
    isCompleteFn: (data) => {
        // Form is complete if both parts are signed
        if (data.length === 2)
            return true;
        // Form is complete if one part is signed and it's a Section 2 or 3 H3
        const section = data[0].data.sectionValue;
        return section === "2" || section === "3";
    },
    extractGenericData: (data) => ({
        admissions: {
            admissionDateTime: data[0]?.data.admissionDateTime,
            admissionHospitalName: data[0]?.data.hospital.name,
            sectionValue: data[0]?.data.sectionValue,
            admissionHospitalAddress: data[0]?.data.hospital.address,
            admissionsHospitalPostcode: data[0]?.data.hospital.postalCode,
            patientWasInHospitalBeforeSectioning: data[0]?.data.patientWasInHospitalBeforeSectioning,
            secondMedRecRecievedDateTime: data[1]?.data.secondMedRecReceivedTime,
            section4H3WithOnlyPart1Completed: data.length === 1 && data[0].data.sectionValue === "4",
        },
        hospital: data[0]?.data.hospital,
    }),
    // H3 needs to be sent to MHL team on both part 1 and part 2
    hospitalMhlAutoRoutePartOverride: [1, 2],
};
//# sourceMappingURL=h3.js.map